import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Plus, Search, Loader2, ArrowRight, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Users, MessageCircle, CalendarPlus, ListOrdered, Receipt, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { openWhatsApp, TEMPLATES } from "../lib/whatsapp";
import QuickBillDialog from "../components/QuickBillDialog";
import FollowUpDialog from "../components/FollowUpDialog";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [clinicName, setClinicName] = useState("our clinic");
  const [defaultFee, setDefaultFee] = useState(500);
  const [upiVpa, setUpiVpa] = useState("");
  const [upiPayeeName, setUpiPayeeName] = useState("");
  const [billFor, setBillFor] = useState(null); // {patient, mode}
  const [followUpFor, setFollowUpFor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get("/clinic/settings").then(({ data }) => {
      setClinicName(data?.name || "our clinic");
      if (data?.default_consultation_fee != null) setDefaultFee(data.default_consultation_fee);
      setUpiVpa(data?.upi_vpa || "");
      setUpiPayeeName(data?.upi_payee_name || data?.name || "");
    }).catch(() => {});
  }, []);

  // FAB → "New patient" handler
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.action === "newPatient") setOpen(true);
    };
    window.addEventListener("hedesk:fab", handler);
    return () => window.removeEventListener("hedesk:fab", handler);
  }, []);

  // Honor navigation state from cross-page FAB triggers
  useEffect(() => {
    if (location.state?.openNewPatient) {
      setOpen(true);
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line
  }, []);

  const sendWhatsApp = (p) => {
    if (!p.phone) {
      toast.error("Patient phone is missing");
      return;
    }
    const msg = TEMPLATES.generalMessage({ patient_name: p.name, clinic_name: clinicName });
    openWhatsApp(p.phone, msg);
  };

  const bookForPatient = (p) => {
    navigate("/appointments", { state: { openBook: true, patient_id: p.patient_id } });
  };

  // Multi-action save handler invoked from PatientDialog after the new patient is created.
  const handleSavedWithAction = async (savedPatient, action) => {
    if (!savedPatient?.patient_id) { load(); return; }
    load();
    if (action === "book") {
      navigate("/appointments", { state: { openBook: true, patient_id: savedPatient.patient_id } });
    } else if (action === "queue") {
      // Add as walk-in to today's queue (uses first available doctor on backend)
      try {
        const { data: doctors } = await api.get("/doctors");
        if (!doctors?.length) { toast.error("No doctors available"); return; }
        const { data } = await api.post("/appointments/walk-in", {
          patient_id: savedPatient.patient_id,
          doctor_id: doctors[0].user_id,
          reason: "Walk-in",
        });
        toast.success(`Added to queue · Token #${data.queue_number}`);
        navigate("/queue");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add to queue");
      }
    } else if (action === "bill") {
      setBillFor({ patient: savedPatient, mode: "bill" });
    } else if (action === "paid") {
      setBillFor({ patient: savedPatient, mode: "paid" });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/patients", { params: q ? { q, limit: 200 } : { limit: 200 } });
      setPatients(data.items || data);
    } catch (e) {
      toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-in" data-testid="patients-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">
            Records
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Patients
          </h1>
          <p className="text-slate-600 mt-1">Manage patient records and visit history.</p>
        </div>
        <Button onClick={() => setImportOpen(true)} variant="outline" data-testid="import-patients-btn">
          <Upload className="w-4 h-4 mr-2" /> Import CSV
        </Button>
        <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-patient-btn">
          <Plus className="w-4 h-4 mr-2" /> Add patient
        </Button>
      </div>

      <Card className="border-slate-200 mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or phone..."
              className="pl-9 h-11"
              data-testid="patient-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : patients.length === 0 ? (
            <div className="p-16 text-center" data-testid="patients-empty-state">
              <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "Manrope" }}>
                {q ? "No matching patients" : "No patients yet"}
              </h3>
              <p className="text-sm text-slate-500 mb-5 max-w-sm mx-auto">
                {q ? "Try a different name or phone number." : "Add your first patient to get started."}
              </p>
              {!q && (
                <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Add first patient
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Age / Gender</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Blood group</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patients.map((p) => (
                  <TableRow key={p.patient_id} data-testid={`patient-row-${p.patient_id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-sky-400 text-white flex items-center justify-center text-sm font-semibold">
                          {p.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{p.name}</div>
                          <div className="text-xs text-slate-500 truncate max-w-[200px]">{p.address}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{p.age}y · <span className="capitalize">{p.gender}</span></span>
                    </TableCell>
                    <TableCell><span className="text-sm">{p.phone}</span></TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">{p.blood_group || "—"}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => sendWhatsApp(p)}
                          data-testid={`whatsapp-patient-${p.patient_id}`}
                          title="Send WhatsApp"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                          onClick={() => bookForPatient(p)}
                          data-testid={`book-patient-${p.patient_id}`}
                          title="New appointment"
                        >
                          <CalendarPlus className="w-4 h-4" />
                        </Button>
                        <Link to={`/patients/${p.patient_id}`}>
                          <Button variant="ghost" size="sm" className="text-blue-600" data-testid={`view-patient-${p.patient_id}`}>
                            View <ArrowRight className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PatientDialog
        open={open}
        onOpenChange={setOpen}
        onSaved={load}
        onSavedWithAction={handleSavedWithAction}
        defaultFee={defaultFee}
      />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <QuickBillDialog
        open={!!billFor}
        onOpenChange={(o) => { if (!o) setBillFor(null); }}
        patient={billFor?.patient}
        mode={billFor?.mode || "bill"}
        defaultAmount={defaultFee}
        upiVpa={upiVpa}
        upiPayeeName={upiPayeeName}
        onCompleted={(inv, opts) => {
          // After paid → prompt follow-up
          if ((billFor?.mode === "paid" || opts?.paid) && billFor?.patient) {
            setFollowUpFor(billFor.patient);
          }
          if (!opts?.keepOpen) setBillFor(null);
        }}
      />
      <FollowUpDialog
        open={!!followUpFor}
        onOpenChange={(o) => { if (!o) setFollowUpFor(null); }}
        patient={followUpFor}
        clinicName={clinicName}
      />
    </div>
  );
}

function ImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState("pick"); // pick | preview | done
  const [loading, setLoading] = useState(false);
  const [skipDup, setSkipDup] = useState(true);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setStep("pick"); setResult(null); };

  useEffect(() => { if (open) reset(); }, [open]);

  const runPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/patients/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setStep("preview");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not parse file");
    } finally { setLoading(false); }
  };

  const runImport = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/patients/import?skip_duplicates=${skipDup}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setStep("done");
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="import-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <FileSpreadsheet className="w-5 h-5 text-blue-600" /> Import patients from CSV
          </DialogTitle>
        </DialogHeader>
        {step === "pick" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-6 text-center">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                id="csv-input"
                data-testid="import-file-input"
              />
              <label htmlFor="csv-input" className="cursor-pointer">
                <span className="text-blue-600 font-medium hover:underline">Choose CSV file</span>
                <span className="text-slate-500 text-sm"> or drop here</span>
              </label>
              {file && <div className="mt-3 text-sm text-slate-700 font-medium">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
            </div>
            <div className="text-xs text-slate-500 space-y-1">
              <div><strong>Required columns:</strong> <code className="bg-slate-100 px-1 rounded">name</code>, <code className="bg-slate-100 px-1 rounded">phone</code></div>
              <div><strong>Optional:</strong> <code className="bg-slate-100 px-1 rounded">age</code>, <code className="bg-slate-100 px-1 rounded">gender</code>, <code className="bg-slate-100 px-1 rounded">address</code>, <code className="bg-slate-100 px-1 rounded">blood_group</code>, <code className="bg-slate-100 px-1 rounded">aadhar_number</code></div>
              <div>Duplicates are detected by phone (last 10 digits). Max 5,000 rows / 5MB.</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={runPreview} disabled={!file || loading} className="bg-blue-600 hover:bg-blue-700" data-testid="import-preview-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Preview
              </Button>
            </DialogFooter>
          </div>
        )}
        {step === "preview" && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2 text-center">
              <KPI label="Total" value={preview.summary.total} />
              <KPI label="Valid" value={preview.summary.valid} color="text-emerald-700" />
              <KPI label="Errors" value={preview.summary.errors} color="text-red-700" />
              <KPI label="Duplicates" value={preview.summary.duplicates} color="text-amber-700" />
            </div>
            <div className="max-h-72 overflow-auto rounded-lg border border-slate-200" data-testid="import-preview-rows">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.index} data-testid={`import-row-${r.index}`}>
                      <TableCell className="text-slate-400 text-xs">{r.index + 1}</TableCell>
                      <TableCell className="font-medium">{r.name || <span className="text-red-500">—</span>}</TableCell>
                      <TableCell className="text-xs font-mono">{r.phone || <span className="text-red-500">—</span>}</TableCell>
                      <TableCell>{r.age ?? "—"}</TableCell>
                      <TableCell>
                        {r.errors.length > 0 ? (
                          <span className="text-xs text-red-600">{r.errors.join(", ")}</span>
                        ) : r.duplicate_of ? (
                          <span className="text-xs text-amber-700">Duplicate of {r.duplicate_name}</span>
                        ) : (
                          <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Ready</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {preview.total_rows > preview.rows.length && (
              <div className="text-xs text-slate-500">Showing first {preview.rows.length} of {preview.total_rows} rows. All will be imported.</div>
            )}
            {preview.summary.duplicates > 0 && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} data-testid="skip-duplicates-checkbox" />
                Skip duplicates (recommended)
              </label>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("pick")}>Back</Button>
              <Button onClick={runImport} disabled={loading || preview.summary.valid === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="import-confirm-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Import {preview.summary.valid - (skipDup ? preview.summary.duplicates : 0)} patient(s)
              </Button>
            </DialogFooter>
          </div>
        )}
        {step === "done" && result && (
          <div className="text-center py-6 space-y-3" data-testid="import-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Import complete</div>
            <div className="text-slate-600 text-sm">
              <span className="font-semibold text-emerald-700">{result.created}</span> created
              {result.skipped > 0 && <> · <span className="text-amber-700">{result.skipped} duplicates skipped</span></>}
              {result.failed > 0 && <> · <span className="text-red-700">{result.failed} failed</span></>}
            </div>
            <Button onClick={() => onOpenChange(false)} className="bg-blue-600 hover:bg-blue-700">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, color = "text-slate-900" }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 bg-white">
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`} style={{ fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}

export function PatientDialog({ open, onOpenChange, onSaved, onSavedWithAction, initial, defaultFee }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: "",
    age: "",
    gender: "male",
    phone: "+91 ",
    address: "",
    blood_group: "",
    aadhar_number: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          name: initial.name || "",
          age: String(initial.age || ""),
          gender: initial.gender || "male",
          phone: initial.phone || "+91 ",
          address: initial.address || "",
          blood_group: initial.blood_group || "",
          aadhar_number: initial.aadhar_number || "",
        });
      } else {
        setForm({ name: "", age: "", gender: "male", phone: "+91 ", address: "", blood_group: "", aadhar_number: "" });
      }
      setError("");
      setSubmittingAction(null);
    }
  }, [open, initial]);

  const savePatient = async () => {
    // Returns the saved patient (created or updated) or throws.
    const aad = (form.aadhar_number || "").replace(/\s/g, "");
    if (aad && !/^\d{12}$/.test(aad)) {
      throw new Error("Aadhar must be 12 digits.");
    }
    const payload = { ...form, aadhar_number: aad, age: parseInt(form.age) };
    if (isEdit) {
      const { data } = await api.put(`/patients/${initial.patient_id}`, payload);
      return data;
    } else {
      const { data } = await api.post("/patients", payload);
      return data;
    }
  };

  const handleSubmitWithAction = async (action) => {
    setError("");
    setSubmitting(true);
    setSubmittingAction(action);
    try {
      const saved = await savePatient();
      toast.success(isEdit ? "Patient updated" : "Patient added");
      onOpenChange(false);
      if (action === "save") {
        onSaved && onSaved(saved);
      } else if (onSavedWithAction) {
        onSavedWithAction(saved, action);
      } else {
        onSaved && onSaved(saved);
      }
    } catch (e) {
      setError(e.message || formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
      setSubmittingAction(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSubmitWithAction("save");
  };

  const QUICK_ACTIONS = [
    { key: "book", label: "Save & Book", icon: CalendarPlus, color: "bg-blue-600 hover:bg-blue-700 text-white" },
    { key: "queue", label: "Save & Queue", icon: ListOrdered, color: "bg-purple-600 hover:bg-purple-700 text-white" },
    { key: "bill", label: "Save & Bill", icon: Receipt, color: "bg-amber-600 hover:bg-amber-700 text-white" },
    { key: "paid", label: "Save & Paid", icon: IndianRupee, color: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="patient-dialog" className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>{isEdit ? "Edit patient" : "Add patient"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="patient-name-input" className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Age</Label>
              <Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} required min="0" max="150" data-testid="patient-age-input" className="mt-1.5" />
            </div>
            <div>
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="patient-gender-select" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Phone (Indian format)</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required placeholder="+91 98765 43210" data-testid="patient-phone-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Blood group</Label>
            <Select value={form.blood_group} onValueChange={(v) => setForm({ ...form, blood_group: v })}>
              <SelectTrigger data-testid="patient-blood-select" className="mt-1.5">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {BLOOD_GROUPS.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Locality, City" data-testid="patient-address-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Aadhar number</Label>
            <Input
              value={form.aadhar_number}
              onChange={(e) => setForm({ ...form, aadhar_number: e.target.value.replace(/[^\d\s]/g, "").slice(0, 14) })}
              placeholder="1234 5678 9012"
              maxLength={14}
              data-testid="patient-aadhar-input"
              className="mt-1.5"
            />
            <p className="text-xs text-slate-400 mt-1">12-digit Aadhar number. Stored encrypted-in-transit.</p>
          </div>
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}

          {!isEdit && (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Quick next steps {defaultFee ? <span className="text-slate-400 font-normal normal-case">· Fee ₹{defaultFee}</span> : null}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_ACTIONS.map((a) => (
                  <Button
                    key={a.key}
                    type="button"
                    onClick={() => handleSubmitWithAction(a.key)}
                    disabled={submitting}
                    className={`h-10 text-sm font-medium ${a.color}`}
                    data-testid={`patient-save-${a.key}`}
                  >
                    {submitting && submittingAction === a.key ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <a.icon className="w-4 h-4 mr-1.5" /> {a.label}
                      </>
                    )}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="patient-submit-btn">
              {submitting && submittingAction === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save" : "Save only")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
